import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarCheck, CheckCircle2, Infinity as InfinityIcon, Sparkles } from 'lucide-react';
import { Eyebrow } from '../components/home/HomeSections';

const API = process.env.REACT_APP_BACKEND_URL;
const inr = (n) => `₹${n.toLocaleString('en-IN')}`;

const supportLines = (p) => {
  const s = p.support;
  if (!s.weeks) return ['45-minute one-to-one session with a consultant', 'Personalized Career Summary', 'Target roles and next steps'];
  return [
    `${s.weeks} weeks of guided support`,
    `${s.checkins} weekly human check-ins`,
    s.milestone_reviews ? `${s.milestone_reviews} consultant milestone review${s.milestone_reviews > 1 ? 's' : ''}` : null,
    s.specialist_reviews ? `${s.specialist_reviews} specialist review` : null,
    'Lifetime access to DIY learning modules',
  ].filter(Boolean);
};

const ProgramCard = ({ p, i }) => {
  const featured = p.kind === 'bundle';
  return (
    <div style={{ animationDelay: `${i * 70}ms` }} data-testid={`program-card-${p.id}`}
      className={`relative rounded-3xl p-7 flex flex-col animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both ${featured ? 'bg-[#0b1f4d] text-white shadow-[0_20px_60px_rgba(11,31,77,0.25)]' : 'bg-white border border-slate-200 hover:border-teal-300 transition-colors'}`}>
      {featured && <span className="absolute -top-3 left-7 inline-flex items-center gap-1 bg-teal-400 text-[#0b1f4d] text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full"><Sparkles className="w-3 h-3" /> Best value</span>}
      <p className={`text-xs font-semibold uppercase tracking-widest ${featured ? 'text-teal-300' : 'text-teal-700'}`}>{p.kind === 'consultation' ? 'Start here' : p.kind === 'bundle' ? 'Bundle' : 'Program'}</p>
      <h3 className={`font-outfit text-xl font-bold mt-2 ${featured ? '' : 'text-[#0b1f4d]'}`}>{p.name}</h3>
      <p className="mt-4">{p.compare_at && <s className="block text-base text-slate-400" data-testid={`program-was-price-${p.id}`}>{inr(p.compare_at)}</s>}<span className="font-outfit text-4xl font-bold" data-testid={`program-price-${p.id}`}>{inr(p.price)}</span>
        <span className={`text-sm ml-2 ${featured ? 'text-slate-300' : 'text-slate-500'}`}>{p.support.weeks ? `${p.support.weeks} weeks` : 'one session'}</span></p>
      <p className={`text-sm leading-relaxed mt-4 ${featured ? 'text-slate-300' : 'text-slate-600'}`}>{p.tagline}</p>
      <ul className="space-y-2.5 mt-6 text-sm">{supportLines(p).map((l) => (
        <li key={l} className="flex gap-2"><CheckCircle2 className={`w-4 h-4 shrink-0 mt-0.5 ${featured ? 'text-teal-300' : 'text-teal-500'}`} />{l}</li>
      ))}</ul>
      <div className="flex-1" />
      <Link to={`/apply?plan=${p.package_id}`} data-testid={`program-buy-${p.id}`}
        className={`group mt-8 inline-flex items-center justify-center gap-2 font-semibold py-3.5 rounded-full transition-[background-color,transform] duration-200 hover:-translate-y-0.5 ${featured ? 'bg-teal-400 hover:bg-teal-300 text-[#0b1f4d]' : 'bg-[#0b1f4d] hover:bg-[#13296b] text-white'}`}>
        {p.kind === 'consultation' ? 'Book consultation' : 'Enroll now'} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
      </Link>
    </div>
  );
};

export default function Programs() {
  const [programs, setPrograms] = useState([]);
  useEffect(() => {
    document.title = 'Programs & Pricing | Elevate Careers';
    fetch(`${API}/api/journey/public/programs`).then((r) => r.json()).then(setPrograms).catch(() => setPrograms([]));
  }, []);
  const consult = programs.filter((p) => p.kind === 'consultation');
  const rest = programs.filter((p) => p.kind !== 'consultation');
  return (
    <main data-testid="programs-page">
      <section className="bg-[#0b1f4d] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <Eyebrow>Programs & pricing</Eyebrow>
          <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-3xl">Pick the support that fits where you are.</h1>
          <p className="mt-6 text-slate-300 max-w-2xl leading-relaxed">Not sure where to begin? Start with a Career Clarity Consultation. Already know what you need? Enroll in any program directly and your dashboard opens right away.</p>
        </div>
      </section>
      <section className="bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
          {!programs.length ? <div className="h-64 rounded-3xl bg-white animate-pulse" /> : (
            <>
              <div className="grid lg:grid-cols-[1fr_2fr] gap-6 items-stretch mb-12">
                {consult.map((p, i) => <ProgramCard key={p.id} p={p} i={i} />)}
                <div className="rounded-3xl border border-dashed border-teal-300 bg-teal-50/60 p-7 flex flex-col justify-center" data-testid="programs-consult-note">
                  <CalendarCheck className="w-6 h-6 text-teal-700" />
                  <p className="font-outfit font-semibold text-lg text-[#0b1f4d] mt-3">Why most students start with a consultation</p>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed max-w-xl">A consultant reviews your Career Readiness Check, confirms your target roles and recommends the program that fits. You can still enroll in any program directly below.</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 pt-3">{rest.map((p, i) => <ProgramCard key={p.id} p={p} i={i + 1} />)}</div>
            </>
          )}
          <p className="mt-10 text-xs text-slate-500 max-w-3xl leading-relaxed flex gap-2" data-testid="programs-terms"><InfinityIcon className="w-4 h-4 shrink-0" />DIY learning modules stay available for as long as Elevate runs the program. Weekly check-ins and reviews are limited to the support period shown. Programs support your preparation and do not guarantee interviews or placement.</p>
        </div>
      </section>
    </main>
  );
}
